import { BaseInputDevice, Debouncer } from './InputDevice.ts';

/**
 * A keyboard key pretending to be an external switch.
 *
 * Purely a development convenience: it lets the whole external-button path --
 * `ExternalButtonController`, debouncing, BUTTON_DOWN/BUTTON_UP logging,
 * latency attribution -- be exercised before any hardware arrives. It reports
 * itself as a distinct device id so sessions recorded this way are never
 * mistaken for real hardware sessions during analysis.
 */
export class KeyboardButtonDevice extends BaseInputDevice {
  readonly id = 'keyboard-button';
  readonly label: string;

  private debouncer = new Debouncer(0);
  private downHandler = (e: KeyboardEvent) => this.handle(e, true);
  private upHandler = (e: KeyboardEvent) => this.handle(e, false);

  constructor(private readonly key = 'KeyB') {
    super();
    this.label = `Keyboard "${key}" as external switch`;
  }

  async connect(): Promise<void> {
    window.addEventListener('keydown', this.downHandler);
    window.addEventListener('keyup', this.upHandler);
    this.status = 'connected';
  }

  async disconnect(): Promise<void> {
    window.removeEventListener('keydown', this.downHandler);
    window.removeEventListener('keyup', this.upHandler);
    this.debouncer.reset();
    this.status = 'disconnected';
  }

  private handle(e: KeyboardEvent, down: boolean): void {
    if (e.code !== this.key) return;
    if (down && e.repeat) return;
    e.preventDefault();
    const timestamp = performance.now();
    if (!this.debouncer.accept(down, timestamp)) return;
    this.emitButton({ down, timestamp, source: this.id });
  }
}
